import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// HIDDEN_SCORE_UI: uncomment with the AnswerConfidenceBadge block near the bottom of this file.
// import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  TablePagination,
} from "@mui/material";
import type { TFunction } from "i18next";
import { useMemo, useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  detailWithoutSqlSection,
  shouldUseTabbedResponse,
  type PipelineVerboseStep,
} from "@/features/chat/lib/turnStream";
import { CardsPanel } from "@/features/chat/ui/CardsPanel";
import { ChartPanel } from "@/features/chat/ui/ChartPanel";
import { ChatMessageTime } from "@/features/chat/ui/ChatMessageTime";
import { ChatProcessingPanel } from "@/features/chat/ui/ChatProcessingPanel";
import { ChatTurnFeedbackButtons } from "@/features/chat/ui/ChatTurnFeedbackButtons";
import { LearningHistoryResolutionNote } from "@/features/chat/ui/LearningHistoryResolutionNote";
import { LearningSuggestionPrompt } from "@/features/chat/ui/LearningSuggestionPrompt";
import type {
  AnswerConfidence,
  ChatChartCollection,
  ChatResultTable,
  ChatTabularData,
  ChatTurnFeedback,
  DocumentAgreement,
  DocumentCitation,
  DocumentConflict,
  DocumentSourceRef,
  GeneratedQuery,
  GeneralizedChartDefinition,
  LearningHistoryResolution,
  LearningSuggestion,
} from "@/features/chat/model/chatTypes";
import type { CitedSource } from "@/shared/lib/citations";
import { CitedText } from "@/shared/ui/CitedText";
import { SourcesList } from "@/shared/ui/SourcesList";
import { JsonCodeMirror } from "@/shared/ui/JsonCodeMirror";
import { SqlCodeMirror } from "@/shared/ui/SqlCodeMirror";
import { useSqlDialect } from "@/shared/hooks/useSqlDialect";
import type { TurnPhase } from "@/features/chat/lib/turnPhase";
import { isCallTimingStep, summarizeTurnTiming } from "@/features/chat/lib/turnTiming";
import { usePermissions } from "@/features/users/hooks/usePermissions";
import { CHAT_TABLE_ROWS_PER_PAGE, PERMISSION } from "@/constants";
import { TurnProgressStrip } from "@/features/chat/ui/TurnProgressStrip";
import { TurnTimingBreakdown } from "@/features/chat/ui/TurnTimingBreakdown";

type ResponseTabId =
  "insights" | "table" | "chart" | "cards" | "sql" | "howGenerated" | "processing";

type ChatAssistantResponseProps = {
  text: string;
  summary?: string | null;
  insights?: string[] | null;
  why?: string | null;
  whyPoints?: string[] | null;
  action?: string | null;
  actionPoints?: string[] | null;
  /** Numbered sources the `why` text cites, shown as one list under the answer. */
  sources?: CitedSource[];
  businessImpact?: string | null;
  verboseLog: PipelineVerboseStep[];
  followUpQuestions: string[];
  tabularData?: ChatTabularData | null;
  chartData?: ChatChartCollection | null;
  generatedQueries?: GeneratedQuery[] | null;
  answerConfidence?: AnswerConfidence | null;
  documentAgreement?: DocumentAgreement | null;
  documentConflicts?: DocumentConflict[];
  documentSources?: string[];
  documentSourceRefs?: DocumentSourceRef[];
  /** Terms resolved from the user's Learning History; each gets a note with a "Change" menu. */
  learningHistoryResolutions?: LearningHistoryResolution[];
  /** A correction or preference the question stated, offered to save as a learning. */
  learningSuggestion?: LearningSuggestion;
  isStreaming?: boolean;
  /** The step the run is on; drives the Processing tab while streaming. */
  currentVerboseStep?: PipelineVerboseStep | null;
  /** The table/chart/SQL shown arrived before the answer validator approved them. */
  answerPartsProvisional?: boolean;
  /** Summary and insights are in; why and action are still being written. */
  narrativePending?: boolean;
  /** Business impact was revised once the why research came in. */
  businessImpactUpdated?: boolean;
  /** The stage a running turn is in, for the Processing tab and the progress strip. */
  phase?: TurnPhase | null;
  /** Seconds since the question was sent, while the turn runs. */
  elapsedSeconds?: number;
  /** When the answer completed or the turn was cancelled, shown under the response. */
  completedAt?: number | null;
  isTerminated?: boolean;
  onFollowUpClick?: (question: string) => void;
  onAddChartToApp?: (chart: GeneralizedChartDefinition) => void;
  canAddChartToApp?: boolean;
  onAddTableToApp?: () => void;
  canAddTableToApp?: boolean;
  onAddCardsToApp?: () => void;
  canAddCardsToApp?: boolean;
  domainId?: number | null;
  /** Identifies the turn for feedback; omitted (or still streaming) hides the thumbs. */
  turnId?: string | null;
  feedback?: ChatTurnFeedback | null;
};

export function ChatAssistantResponse({
  text,
  summary = null,
  insights = null,
  why = null,
  whyPoints = null,
  action = null,
  actionPoints = null,
  sources = [],
  businessImpact = null,
  verboseLog,
  followUpQuestions,
  tabularData = null,
  chartData = null,
  generatedQueries = null,
  answerConfidence = null,
  documentAgreement = null,
  documentConflicts = [],
  documentSources = [],
  documentSourceRefs = [],
  learningHistoryResolutions = [],
  learningSuggestion,
  isStreaming = false,
  currentVerboseStep = null,
  answerPartsProvisional = false,
  narrativePending = false,
  businessImpactUpdated = false,
  isTerminated = false,
  onFollowUpClick,
  onAddChartToApp,
  canAddChartToApp = false,
  onAddTableToApp,
  canAddTableToApp = false,
  onAddCardsToApp,
  canAddCardsToApp = false,
  domainId = null,
  turnId = null,
  feedback = null,
  phase = null,
  elapsedSeconds = 0,
  completedAt = null,
}: ChatAssistantResponseProps) {
  const { t } = useTranslation("chat");
  const endedTime =
    completedAt !== null && !isStreaming ? (
      <ChatMessageTime at={completedAt} outcome={isTerminated ? "cancelled" : "completed"} />
    ) : null;
  // Each LLM and tool call's timing sits behind chat-call-timing-view, held on the chat's domain.
  // Unreleased, so a Super Administrator for now; the backend sends the per-call timing rows to
  // nobody else.
  const { hasForDomain } = usePermissions();
  const showCallTimings = hasForDomain(PERMISSION.CHAT_CALL_TIMING_VIEW, domainId);
  const displayText =
    text.trim() || (isStreaming ? "" : t("assistantPlaceholder"));

  const useTabbed =
    shouldUseTabbedResponse({
      text: displayText,
      verboseLog,
      followUpQuestions,
    }) ||
    Boolean(tabularData) ||
    Boolean(chartData?.charts?.length) ||
    Boolean(chartData?.cards?.length) ||
    Boolean(generatedQueries?.length) ||
    isTerminated ||
    // A running turn always shows tabs: its progress lives in the Processing tab.
    isStreaming;

  const visibleTabs = useMemo(() => {
    const tabs: Array<{
      id: ResponseTabId;
      label: string;
      icon: ReactElement;
    }> = [
      {
        id: "insights",
        label: t("response.tabs.insights"),
        icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />,
      },
    ];
    if (tabularData && tabularData.columns.length > 0) {
      tabs.push({
        id: "table",
        label: t("response.tabs.table"),
        icon: <TableChartOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    if (chartData && chartData.charts.length > 0) {
      tabs.push({
        id: "chart",
        label: t("response.tabs.chart"),
        icon: <BarChartOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    if (chartData && chartData.cards && chartData.cards.length > 0) {
      tabs.push({
        id: "cards",
        label: t("response.tabs.cards"),
        icon: <ViewModuleOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    if (generatedQueries && generatedQueries.length > 0) {
      tabs.push({
        id: "sql",
        label: t("response.tabs.sql"),
        icon: <DataObjectOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    // While the turn runs, its live progress takes the place of "How it was generated": one
    // box for the whole answer instead of a processing panel stacked above it.
    if (isStreaming) {
      tabs.push({
        id: "processing",
        label: t("response.tabs.processing"),
        icon: <CircularProgress size={12} thickness={5} sx={{ color: "var(--theme-primary)" }} />,
      });
    } else if (verboseLog.length > 0 || isTerminated) {
      // A stopped turn keeps the tab even with an empty log: the panel is where the user
      // finds out how far the run got, and "nothing recorded" is itself the answer.
      tabs.push({
        id: "howGenerated",
        label: t("response.tabs.howGenerated"),
        icon: <BuildOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    return tabs;
  }, [
    chartData,
    generatedQueries,
    isStreaming,
    isTerminated,
    t,
    tabularData,
    verboseLog.length,
  ]);

  // Nothing written for the Insights tab yet: no answer text and no summary/insights part.
  const isWritingAnswer =
    isStreaming && !text.trim() && !summary && !(insights && insights.length > 0);
  const firstReadyDataTab = visibleTabs.find(
    (tab) => tab.id === "table" || tab.id === "chart",
  )?.id;

  // The tab follows the answer until the user picks one. A running turn opens on Processing,
  // moves to the data the moment a table or chart lands (the text is still 30-50 s away, and
  // a spinner over ready rows made the wait feel like the whole turn), goes back to
  // Processing if a re-run empties the data, and lands on Insights when the text arrives.
  // A tab the user chose is kept for as long as it exists.
  const [userTab, setUserTab] = useState<ResponseTabId | null>(null);
  const autoTab: ResponseTabId = isStreaming ? (firstReadyDataTab ?? "processing") : "insights";
  const effectiveTab =
    userTab !== null && visibleTabs.some((tab) => tab.id === userTab) ? userTab : autoTab;
  const isDataTab =
    effectiveTab === "table" ||
    effectiveTab === "chart" ||
    effectiveTab === "cards" ||
    effectiveTab === "sql";

  // Feedback needs a real, server-assigned turn id: while a turn is still streaming, `turnId`
  // is briefly the client-generated placeholder created before the request resolved.
  const showFeedback = Boolean(turnId) && !isStreaming;
  const learningSuggestionPrompt = (
    <LearningSuggestionPrompt
      suggestion={learningSuggestion}
      turnId={turnId}
      canAnswer={!isStreaming && !isTerminated}
    />
  );
  const learningHistoryNote = (
    <LearningHistoryResolutionNote
      resolutions={learningHistoryResolutions}
      canChange={!isStreaming && !isTerminated}
      turnId={turnId}
      onAskQuestion={onFollowUpClick}
    />
  );

  if (!useTabbed) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            width: "100%",
          }}
        >
          <AvatarIcon />
          <MessageBubble>
            {learningHistoryNote}
            {isTerminated ? (
              <TerminatedInsightsMessage />
            ) : (
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {displayText}
                {isStreaming ? <StreamingCursor /> : null}
              </Typography>
            )}
          </MessageBubble>
        </Box>
        {endedTime ? <Box sx={{ pl: 4.5 }}>{endedTime}</Box> : null}
        <Box sx={{ pl: 5 }}>{learningSuggestionPrompt}</Box>
        {showFeedback && turnId ? (
          <Stack direction="row" justifyContent="flex-end">
            <ChatTurnFeedbackButtons turnId={turnId} feedback={feedback} />
          </Stack>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        width: "100%",
      }}
    >
      <AvatarIcon />
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: "100%",
            borderRadius: 2,
            border: "1px solid var(--theme-border)",
            bgcolor: "var(--theme-surface)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid var(--theme-border)",
            }}
          >
            <Tabs
              value={effectiveTab}
              onChange={(_, value: ResponseTabId) => setUserTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 40,
                px: 1,
                "& .MuiTabs-indicator": {
                  backgroundColor: "var(--theme-primary)",
                },
                "& .MuiTab-root": {
                  minHeight: 40,
                  minWidth: "auto",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  color: "var(--theme-text-secondary)",
                  gap: 0.5,
                  "&.Mui-selected": {
                    color: "var(--theme-primary)",
                  },
                },
              }}
            >
              {visibleTabs.map((tab) => (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
            {showFeedback && turnId ? (
              <Box sx={{ flexShrink: 0, pr: 1 }}>
                <ChatTurnFeedbackButtons turnId={turnId} feedback={feedback} />
              </Box>
            ) : null}
          </Box>

          <Box sx={{ px: 2, py: 1.5 }}>
            {learningHistoryNote}
            {isStreaming && isDataTab ? (
              <TurnProgressStrip
                phase={phase}
                elapsedSeconds={elapsedSeconds}
                onShowDetails={() => setUserTab("processing")}
              />
            ) : null}
            {answerPartsProvisional && isDataTab ? <VerifyingAnswerNotice /> : null}
            {effectiveTab === "processing" ? (
              <ChatProcessingPanel
                embedded
                currentVerboseStep={currentVerboseStep ?? verboseLog.at(-1) ?? null}
                verboseLog={verboseLog}
                domainId={domainId}
                phase={phase}
                elapsedSeconds={elapsedSeconds}
              />
            ) : null}
            {effectiveTab === "insights" ? (
              isTerminated ? (
                <TerminatedInsightsMessage />
              ) : isWritingAnswer ? (
                <WritingInsightsNotice dataIsReady={Boolean(firstReadyDataTab)} />
              ) : (
                <InsightsPanel
                  displayText={displayText}
                  summary={summary}
                  insights={insights}
                  why={why}
                  whyPoints={whyPoints}
                  action={action}
                  actionPoints={actionPoints}
                  sources={sources}
                  businessImpact={businessImpact}
                  followUpQuestions={followUpQuestions}
                  answerConfidence={answerConfidence}
                  documentAgreement={documentAgreement}
                  documentConflicts={documentConflicts}
                  documentSources={documentSources}
                  documentSourceRefs={documentSourceRefs}
                  isStreaming={isStreaming}
                  narrativePending={isStreaming && narrativePending}
                  businessImpactUpdated={businessImpactUpdated}
                  onFollowUpClick={onFollowUpClick}
                />
              )
            ) : null}

            {effectiveTab === "table" && tabularData ? (
              <DataTable
                tabularData={tabularData}
                onAddToApp={canAddTableToApp ? onAddTableToApp : undefined}
              />
            ) : null}

            {effectiveTab === "chart" && chartData ? (
              <ChartPanel
                charts={chartData.charts}
                data={chartData.data}
                onAddToApp={canAddChartToApp ? onAddChartToApp : undefined}
              />
            ) : null}

            {effectiveTab === "cards" && chartData?.cards ? (
              <CardsPanel
                cards={chartData.cards}
                onAddToApp={canAddCardsToApp ? onAddCardsToApp : undefined}
              />
            ) : null}

            {effectiveTab === "sql" && generatedQueries ? (
              <SqlQueriesPanel queries={generatedQueries} domainId={domainId} />
            ) : null}

            {effectiveTab === "howGenerated" ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TurnTimingBreakdown
                  timing={summarizeTurnTiming(verboseLog, { includeCalls: showCallTimings })}
                />
                {/* A call recorded for its timing alone belongs to the breakdown above, not here. */}
                {verboseLog.filter((step) => !isCallTimingStep(step)).map((step) =>
                  step.eventType === "llm_call" && hasRawIo(step) ? (
                    <LlmCallStep
                      key={`${step.stepIndex}-${step.eventType}`}
                      step={step}
                    />
                  ) : (step.eventType === "rag_retrieval" ||
                      step.eventType === "graph_search") &&
                    hasRawIo(step) ? (
                    <RetrievalStep
                      key={`${step.stepIndex}-${step.eventType}`}
                      step={step}
                    />
                  ) : step.sql ? (
                    <GeneratedSqlStep
                      key={`${step.stepIndex}-${step.eventType}`}
                      step={step}
                      domainId={domainId}
                    />
                  ) : (
                    <Box key={`${step.stepIndex}-${step.eventType}`}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {step.stepTitle}
                      </Typography>
                      {step.detail ? (
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: "pre-wrap",
                            color: "var(--theme-text-secondary)",
                            mt: 0.5,
                          }}
                        >
                          {step.detail}
                        </Typography>
                      ) : null}
                    </Box>
                  ),
                )}
                {isTerminated ? <TerminatedHowGeneratedNotice /> : null}
              </Box>
            ) : null}
            {learningSuggestionPrompt}
          </Box>
        </Box>
        {endedTime}
      </Box>
    </Box>
  );
}

const ACCORDION_SX = {
  bgcolor: "transparent",
  boxShadow: "none",
  border: "1px solid var(--theme-border)",
  borderRadius: "6px !important",
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
} as const;

const ACCORDION_SUMMARY_SX = {
  minHeight: 36,
  px: 1.5,
  "&.Mui-expanded": { minHeight: 36 },
  "& .MuiAccordionSummary-content": { my: 0.75 },
} as const;

function parseJsonStructure(
  value: string,
): Record<string, unknown> | unknown[] | null {
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed !== null && typeof parsed === "object"
      ? (parsed as Record<string, unknown> | unknown[])
      : null;
  } catch {
    return null;
  }
}

function noop() {
  // JsonCodeMirror requires onChange even when readOnly.
}

// With APP_CHAT_VERBOSE_HEADINGS_ONLY on, the backend withholds the raw LLM/retrieval
// payloads, so a request/response accordion would open onto empty panes. Fall back to the
// plain heading row when there is nothing to show.
function hasRawIo(step: PipelineVerboseStep): boolean {
  return Boolean(
    (step.messages && step.messages.length > 0) ||
    step.rawResponse ||
    step.input ||
    step.output,
  );
}

function RawIoStep({
  stepTitle,
  requestText,
  responseText,
  responseIsJson,
}: {
  stepTitle: string;
  requestText: string;
  responseText: string;
  responseIsJson: boolean;
}) {
  const { t } = useTranslation("chat");

  return (
    <Accordion disableGutters sx={ACCORDION_SX}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={ACCORDION_SUMMARY_SX}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {stepTitle}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 1.5,
          pt: 0,
          pb: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "var(--theme-text-secondary)" }}
        >
          {t("response.howGenerated.request")}
        </Typography>
        <JsonCodeMirror
          value={requestText}
          onChange={noop}
          readOnly
          maxHeight="200px"
        />
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "var(--theme-text-secondary)" }}
        >
          {t("response.howGenerated.response")}
        </Typography>
        {responseIsJson ? (
          <JsonCodeMirror
            value={responseText}
            onChange={noop}
            readOnly
            maxHeight="200px"
          />
        ) : (
          <SqlCodeMirror value={responseText} readOnly maxHeight="200px" />
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function LlmCallStep({ step }: { step: PipelineVerboseStep }) {
  const requestText = useMemo(() => {
    const messages = step.messages ?? [];
    const formattedMessages = messages.map((message) => {
      const parsedContent = parseJsonStructure(message.content);
      return parsedContent !== null
        ? { ...message, content: parsedContent }
        : message;
    });
    return JSON.stringify(formattedMessages, null, 2);
  }, [step.messages]);

  const response = useMemo(() => {
    const rawResponse = step.rawResponse ?? "";
    const parsedResponse = parseJsonStructure(rawResponse);
    return parsedResponse !== null
      ? { text: JSON.stringify(parsedResponse, null, 2), isJson: true }
      : { text: rawResponse, isJson: false };
  }, [step.rawResponse]);

  return (
    <RawIoStep
      stepTitle={step.stepTitle}
      requestText={requestText}
      responseText={response.text}
      responseIsJson={response.isJson}
    />
  );
}

function RetrievalStep({ step }: { step: PipelineVerboseStep }) {
  const requestText = useMemo(
    () => JSON.stringify(step.input ?? {}, null, 2),
    [step.input],
  );
  const responseText = useMemo(
    () => JSON.stringify(step.output ?? {}, null, 2),
    [step.output],
  );

  return (
    <RawIoStep
      stepTitle={step.stepTitle}
      requestText={requestText}
      responseText={responseText}
      responseIsJson
    />
  );
}

// A SQL generation attempt keeps its statement in the step payload, untrimmed. The detail body
// carries the rest of the trace (purpose, validation errors, timing) and, on turns generated
// before the trim was removed, a cut-off copy of the statement -- dropped here in favour of the
// payload so the panel always shows the query in full.
function GeneratedSqlStep({
  step,
  domainId,
}: {
  step: PipelineVerboseStep;
  domainId?: number | null;
}) {
  const dialect = useSqlDialect(domainId);
  const detail = useMemo(
    () => detailWithoutSqlSection(step.detail),
    [step.detail],
  );

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {step.stepTitle}
      </Typography>
      {detail ? (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "pre-wrap",
            color: "var(--theme-text-secondary)",
            mt: 0.5,
          }}
        >
          {detail}
        </Typography>
      ) : null}
      <Box sx={{ mt: 1 }}>
        <SqlCodeMirror
          value={step.sql ?? ""}
          readOnly
          maxHeight="320px"
          dialect={dialect}
        />
      </Box>
    </Box>
  );
}

function InsightsPanel({
  displayText,
  summary,
  insights,
  why,
  whyPoints,
  action,
  actionPoints,
  sources,
  businessImpact,
  followUpQuestions,
  // HIDDEN_SCORE_UI: still passed in by ChatPage and still declared in the props type below;
  // uncomment with the AnswerConfidenceBadge render site further down.
  // answerConfidence,
  documentAgreement,
  documentConflicts,
  documentSources,
  documentSourceRefs,
  isStreaming,
  narrativePending,
  businessImpactUpdated,
  onFollowUpClick,
}: {
  displayText: string;
  summary: string | null;
  insights: string[] | null;
  why: string | null;
  whyPoints: string[] | null;
  action: string | null;
  actionPoints: string[] | null;
  sources: CitedSource[];
  businessImpact: string | null;
  followUpQuestions: string[];
  answerConfidence?: AnswerConfidence | null;
  documentAgreement?: DocumentAgreement | null;
  documentConflicts?: DocumentConflict[];
  documentSources?: string[];
  documentSourceRefs?: DocumentSourceRef[];
  isStreaming: boolean;
  /** Summary and insights are in; why and action are still being written after the research. */
  narrativePending: boolean;
  /** Business impact was revised once the why research came in. */
  businessImpactUpdated: boolean;
  onFollowUpClick?: (question: string) => void;
}) {
  const { t } = useTranslation("chat");
  // Structured as soon as the insights exist -- they arrive mid-turn as an answer part, well
  // before the answer text -- so each section shows the moment it is written.
  const isStructured = insights !== null && insights.length > 0;

  /* Built once and placed under the insights in both branches: what the documents added is worth
     showing whether or not the answer came back in the structured shape, and it reads as backing
     for the answer rather than as a preamble to it. */
  const documentEvidence = !isStreaming ? (
    <DocumentEvidence
      agreement={documentAgreement ?? null}
      conflicts={documentConflicts ?? []}
      sources={documentSources ?? []}
      sourceRefs={documentSourceRefs ?? []}
    />
  ) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {isStructured ? (
        <>
          {/* HIDDEN_SCORE_UI: the "High/Medium/Low confidence (NN%)" badge on every structured
              chat answer. Scores are hidden app-wide until the scoring behind them is trusted
              again; `grep -rn HIDDEN_SCORE_UI src` lists every hidden spot. Uncomment this line
              together with the AnswerConfidenceBadge component near the bottom of this file and
              the VerifiedOutlinedIcon import at the top.
          {answerConfidence ? <AnswerConfidenceBadge confidence={answerConfidence} /> : null}
          */}

          {summary ? (
            <Typography
              variant="body2"
              sx={{ color: "var(--theme-text-primary)" }}
            >
              {summary}
            </Typography>
          ) : null}

          <Accordion defaultExpanded disableGutters sx={ACCORDION_SX}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon fontSize="small" />}
              sx={ACCORDION_SUMMARY_SX}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("response.sections.insights")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
              <Box
                component="ul"
                sx={{
                  pl: 2,
                  m: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {insights.map((item, index) => (
                  <Typography key={index} component="li" variant="body2">
                    {item}
                  </Typography>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          {documentEvidence}

          {narrativePending ? <NarrativePendingNotice /> : null}

          {why || (whyPoints && whyPoints.length > 0) ? (
            <Accordion disableGutters sx={ACCORDION_SX}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                sx={ACCORDION_SUMMARY_SX}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("response.sections.why")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                {/* Whatever the web research established is folded into the drivers by the
                    backend, each already carrying its `[n]` markers. With no research the text
                    has no markers and this renders as plain prose. */}
                <WhyDrivers why={why} whyPoints={whyPoints} sources={sources} />
              </AccordionDetails>
            </Accordion>
          ) : null}

          {action || (actionPoints && actionPoints.length > 0) ? (
            <Accordion disableGutters sx={ACCORDION_SX}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                sx={ACCORDION_SUMMARY_SX}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("response.sections.action")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                <ActionRecommendations
                  action={action}
                  actionPoints={actionPoints}
                />
              </AccordionDetails>
            </Accordion>
          ) : null}

          {businessImpact ? (
            <Accordion disableGutters sx={ACCORDION_SX}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                sx={ACCORDION_SUMMARY_SX}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("response.sections.businessImpact")}
                  </Typography>
                  {businessImpactUpdated ? (
                    <Chip
                      size="small"
                      label={t("response.parts.updatedWithResearch")}
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        color: "var(--theme-info)",
                        bgcolor: "color-mix(in srgb, var(--theme-info) 12%, transparent)",
                        border: "1px solid color-mix(in srgb, var(--theme-info) 35%, transparent)",
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  ) : null}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                <Typography variant="body2">{businessImpact}</Typography>
              </AccordionDetails>
            </Accordion>
          ) : null}

          {/* Last, and collapsed: the numbered list the `[n]` markers above point at. Rendered
              only when research produced something verifiable, so an answer drawing on nothing
              external looks exactly as it always has. */}
          {sources.length > 0 ? (
            <Accordion disableGutters sx={ACCORDION_SX}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                sx={ACCORDION_SUMMARY_SX}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <LinkOutlinedIcon
                    sx={{ fontSize: 16, color: "var(--theme-text-secondary)" }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("response.sections.sources", { count: sources.length })}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                <SourcesList sources={sources} />
              </AccordionDetails>
            </Accordion>
          ) : null}
        </>
      ) : (
        <>
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", color: "var(--theme-text-primary)" }}
          >
            {displayText}
            {isStreaming ? <StreamingCursor /> : null}
          </Typography>
          {documentEvidence}
        </>
      )}

      {!isStreaming && followUpQuestions.length > 0 ? (
        <Box sx={{ borderTop: "1px solid var(--theme-border)", pt: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: "var(--theme-text-secondary)",
              mb: 1,
              display: "block",
            }}
          >
            {t("response.followUps.suggested")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {followUpQuestions.map((question) => (
              <Chip
                key={question}
                label={question}
                size="small"
                clickable={Boolean(onFollowUpClick)}
                onClick={
                  onFollowUpClick ? () => onFollowUpClick(question) : undefined
                }
                sx={{
                  height: "auto",
                  py: 0.5,
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    display: "block",
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

/** What drove the movement: one bullet per driver, and the single paragraph it has always been
 * for a turn answered before `why_points` existed. Each bullet resolves its own `[n]` markers
 * against the turn's shared source list, so a driver established by research links to the source
 * that established it. */
function WhyDrivers({
  why,
  whyPoints,
  sources,
}: {
  why: string | null;
  whyPoints: string[] | null;
  sources: CitedSource[];
}) {
  if (whyPoints && whyPoints.length > 0) {
    return (
      <Box
        component="ul"
        sx={{ pl: 2, m: 0, display: "flex", flexDirection: "column", gap: 0.5 }}
      >
        {whyPoints.map((point, index) => (
          <Typography key={index} component="li" variant="body2">
            <CitedText text={point} sources={sources} />
          </Typography>
        ))}
      </Box>
    );
  }
  return (
    <Typography variant="body2">
      <CitedText text={why ?? ""} sources={sources} />
    </Typography>
  );
}

/** The recommendations themselves: bullets when the turn carries points, and the single
 * paragraph it has always been for a turn answered before `action_points` existed. */
function ActionRecommendations({
  action,
  actionPoints,
}: {
  action: string | null;
  actionPoints: string[] | null;
}) {
  if (actionPoints && actionPoints.length > 0) {
    return (
      <Box
        component="ul"
        sx={{ pl: 2, m: 0, display: "flex", flexDirection: "column", gap: 0.5 }}
      >
        {actionPoints.map((point, index) => (
          <Typography key={index} component="li" variant="body2">
            {point}
          </Typography>
        ))}
      </Box>
    );
  }
  return <Typography variant="body2">{action}</Typography>;
}

const DOCUMENT_AGREEMENT_COLOR_TOKEN: Partial<
  Record<DocumentAgreement, string>
> = {
  supports: "var(--theme-success)",
  complements: "var(--theme-info, var(--theme-primary))",
  conflicts: "var(--theme-info, var(--theme-primary))",
  document_only: "var(--theme-info, var(--theme-primary))",
};

/**
 * Document provenance for an answer: which uploaded files it drew on, and anything those files
 * state about a figure the warehouse also measured. That extra statement is presented as
 * additional information with its source, never as an alert — the warehouse answer stands on its
 * own, and a document reporting another number adds context to it rather than casting doubt on it.
 * Same-period and different-period differences read alike for that reason.
 *
 * Collapsed by default, and placed under the insights rather than above them. All of it — the
 * extra-information section, the source chips and the page images — is provenance a reader reaches
 * for after the answer, and unfolded above the answer it pushed the answer itself off the screen.
 * The agreement verdict rides in the header so the reader can see documents were involved without
 * opening anything.
 *
 * `sourceRefs` carries page numbers and preview locations; `sources` is the bare filename list the
 * pipeline has always written. Refs are preferred when present and the filenames are the fallback,
 * so a turn answered before the refs existed still shows where it read from.
 */
function DocumentEvidence({
  agreement,
  conflicts,
  sources,
  sourceRefs,
}: {
  agreement: DocumentAgreement | null;
  conflicts: DocumentConflict[];
  sources: string[];
  sourceRefs: DocumentSourceRef[];
}) {
  const { t } = useTranslation("chat");
  const showAgreement = agreement !== null && agreement !== "not_relevant";
  // Memoised for its identity, not its cost: the preview strip fetches on this list changing, and a
  // fresh array every render would refetch on every render — including the ones its own fetch causes.
  const effectiveRefs = useMemo<DocumentSourceRef[]>(
    () =>
      sourceRefs.length > 0
        ? sourceRefs
        : sources.map((source) => ({ sourceFile: source, isImage: false })),
    [sourceRefs, sources],
  );

  if (!showAgreement && conflicts.length === 0 && effectiveRefs.length === 0) {
    return null;
  }

  const agreementColor = agreement
    ? DOCUMENT_AGREEMENT_COLOR_TOKEN[agreement]
    : undefined;

  return (
    <Accordion disableGutters sx={ACCORDION_SX}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={ACCORDION_SUMMARY_SX}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t("response.documents.contextTitle")}
          </Typography>
          {showAgreement && agreementColor ? (
            <Chip
              size="small"
              icon={
                <DescriptionOutlinedIcon
                  sx={{ fontSize: 14, color: `${agreementColor} !important` }}
                />
              }
              label={t(`response.documents.agreement.${agreement}`)}
              sx={{
                height: 22,
                fontSize: "0.7rem",
                fontWeight: 600,
                bgcolor: `color-mix(in srgb, ${agreementColor} 12%, transparent)`,
                color: agreementColor,
                border: `1px solid color-mix(in srgb, ${agreementColor} 35%, transparent)`,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 1.5,
          pt: 0,
          pb: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {conflicts.length > 0 ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "var(--theme-surface-hover)",
              border: "1px solid var(--theme-border)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <InfoOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: "var(--theme-text-secondary)",
                  mt: 0.25,
                }}
                aria-hidden
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("response.documents.extraInfoTitle")}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--theme-text-secondary)",
                    display: "block",
                    mt: 0.25,
                  }}
                >
                  {t("response.documents.extraInfoHelp")}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.5 }}
            >
              {conflicts.map((conflict, index) => (
                <Box
                  key={`${conflict.subject}-${conflict.documentValue}-${index}`}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {conflict.subject}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                    {t("response.documents.dataSays")}:{" "}
                    {conflict.structuredValue}
                    {conflict.structuredSource
                      ? ` (${conflict.structuredSource})`
                      : ""}
                  </Typography>
                  <Typography variant="body2">
                    {t("response.documents.documentsSay")}:{" "}
                    {conflict.documentValue}
                    {formatCitations(conflict.documentSources, t)}
                  </Typography>
                  {/* Who the document itself credits the figure to — a report restating a
                      third party's number ("Source: Newzoo") is a different kind of statement
                      from one measuring it, and the filename alone does not say which it is. */}
                  {conflict.documentStatedSource ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "var(--theme-text-secondary)",
                        display: "block",
                        mt: 0.25,
                      }}
                    >
                      {t("response.documents.documentStatedSource")}:{" "}
                      {conflict.documentStatedSource}
                    </Typography>
                  ) : null}
                  {/* Shown when the pipeline found one, and left off when it did not. The old
                      panel filled the gap with "no explanation could be identified", which read
                      as a caution about the figure rather than as the extra detail this is. */}
                  {conflict.likelyCause ? (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "var(--theme-text-secondary)",
                        display: "block",
                        mt: 0.25,
                      }}
                    >
                      {t("response.documents.likelyCause")}:{" "}
                      {conflict.likelyCause}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}

        {effectiveRefs.length > 0 ? (
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "var(--theme-text-secondary)",
                display: "block",
                mb: 0.5,
              }}
            >
              {t("response.documents.sourcesTitle")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {effectiveRefs.map((ref) => (
                <Chip
                  key={`${ref.sourceFile}-${ref.page ?? "file"}`}
                  size="small"
                  icon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={sourceRefLabel(ref, t)}
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              ))}
            </Box>
            <DocumentPagePreviews refs={effectiveRefs} />
          </Box>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
}

/** ` (file.pdf, p. 3; other.pdf)`, or `""` for an uncited value — appended after a value inline. */
function formatCitations(
  citations: DocumentCitation[],
  t: TFunction<"chat">,
): string {
  if (citations.length === 0) {
    return "";
  }
  const rendered = citations.map((citation) =>
    citation.page
      ? `${citation.sourceFile}, ${t("response.documents.page", { page: citation.page })}`
      : citation.sourceFile,
  );
  return ` (${rendered.join("; ")})`;
}

/** `file.pdf · p. 3`, or just the filename when the parse produced no page number. */
function sourceRefLabel(
  sourceRef: DocumentSourceRef,
  t: TFunction<"chat">,
): string {
  return sourceRef.page
    ? `${sourceRef.sourceFile} · ${t("response.documents.page", { page: sourceRef.page })}`
    : sourceRef.sourceFile;
}

/**
 * The cited pages that were retrieved as images — slides and figures whose numbers are in the page
 * and nowhere in the document's text, so the page itself is the only way to check a claim against
 * what was actually on it.
 *
 * Rendered inline from the `data:` URI the turn carries, and clicking one enlarges it in place. No
 * link to the file and no download: the point is to see the passage the answer was read off, and
 * fetching a whole PDF to find it again is not that.
 */
function DocumentPagePreviews({ refs }: { refs: DocumentSourceRef[] }) {
  const { t } = useTranslation("chat");
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const previewable = refs.filter((ref) => Boolean(ref.previewImage));
  if (previewable.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        sx={{ color: "var(--theme-text-secondary)", display: "block", mb: 0.5 }}
      >
        {t("response.documents.pagePreviewsTitle")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "flex-start",
        }}
      >
        {previewable.map((ref) => {
          const key = `${ref.sourceFile}-${ref.page ?? "file"}`;
          const isExpanded = expandedKey === key;
          const label = sourceRefLabel(ref, t);
          return (
            <Tooltip
              key={key}
              title={
                isExpanded
                  ? t("response.documents.collapsePage")
                  : t("response.documents.expandPage")
              }
            >
              <Box
                component="button"
                type="button"
                aria-label={label}
                aria-expanded={isExpanded}
                onClick={() => setExpandedKey(isExpanded ? null : key)}
                sx={{
                  p: 0,
                  border: "1px solid var(--theme-border)",
                  borderRadius: 1,
                  overflow: "hidden",
                  lineHeight: 0,
                  cursor: "pointer",
                  bgcolor: "transparent",
                  display: "block",
                }}
              >
                <Box
                  component="img"
                  src={ref.previewImage ?? undefined}
                  alt={label}
                  sx={{
                    display: "block",
                    width: isExpanded ? "100%" : 200,
                    maxWidth: isExpanded ? 720 : 200,
                    maxHeight: isExpanded ? "none" : 150,
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

/* HIDDEN_SCORE_UI: the chat answer confidence badge. Commented out along with its only render
   site above (search HIDDEN_SCORE_UI in this file) because an unused component trips
   noUnusedLocals. The answerConfidence prop is still threaded in from ChatPage, so restoring is
   just a matter of uncommenting this block, that render site, and the VerifiedOutlinedIcon import.

const CONFIDENCE_LEVEL_COLOR_TOKEN: Record<AnswerConfidence["level"], string> = {
  high: "var(--theme-success)",
  medium: "var(--theme-warning)",
  low: "var(--theme-error)",
};

function AnswerConfidenceBadge({ confidence }: { confidence: AnswerConfidence }) {
  const { t } = useTranslation("chat");
  const color = CONFIDENCE_LEVEL_COLOR_TOKEN[confidence.level];
  const label = t(`response.confidence.levels.${confidence.level}`, {
    score: Math.round(confidence.score * 100),
  });
  const tooltip =
    confidence.signals.length > 0
      ? confidence.signals.join(" · ")
      : t("response.confidence.noSignals");

  return (
    <Box sx={{ display: "flex" }}>
      <Tooltip title={tooltip}>
        <Chip
          size="small"
          icon={<VerifiedOutlinedIcon sx={{ fontSize: 14, color: `${color} !important` }} />}
          label={label}
          sx={{
            height: 22,
            fontSize: "0.7rem",
            fontWeight: 600,
            bgcolor: `color-mix(in srgb, ${color} 12%, transparent)`,
            color,
            border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      </Tooltip>
    </Box>
  );
}
*/

function TerminatedInsightsMessage() {
  const { t } = useTranslation("chat");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        p: 1.5,
        borderRadius: 1,
        bgcolor:
          "color-mix(in srgb, var(--theme-warning, #ed6c02) 12%, transparent)",
        border:
          "1px solid color-mix(in srgb, var(--theme-warning, #ed6c02) 35%, transparent)",
      }}
    >
      <StopCircleOutlinedIcon
        sx={{ fontSize: 20, color: "var(--theme-warning, #ed6c02)", mt: 0.25 }}
        aria-hidden
      />
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "var(--theme-text-primary)" }}
        >
          {t("terminated.title")}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "var(--theme-text-secondary)", mt: 0.5 }}
        >
          {t("terminated.message")}
        </Typography>
      </Box>
    </Box>
  );
}

function TerminatedHowGeneratedNotice() {
  const { t } = useTranslation("chat");

  return (
    <Box
      sx={{
        mt: 0.5,
        p: 1.5,
        borderRadius: 1,
        bgcolor:
          "color-mix(in srgb, var(--theme-warning, #ed6c02) 12%, transparent)",
        border:
          "1px solid color-mix(in srgb, var(--theme-warning, #ed6c02) 35%, transparent)",
      }}
    >
      <Typography variant="body2" sx={{ color: "var(--theme-text-secondary)" }}>
        {t("terminated.howGenerated")}
      </Typography>
    </Box>
  );
}

function ResultTable({ table }: { table: ChatResultTable }) {
  const { t } = useTranslation("chat");
  const {
    columns,
    rows,
    truncated,
    row_count: rowCount,
    shown_rows: shownRows,
  } = table;
  const total = rowCount ?? rows.length;
  const shown = shownRows ?? rows.length;
  // Paged in the browser: a 114-row breakdown is read ten rows at a time, not scrolled.
  // The page is clamped rather than reset so a re-run that trims the result (a validator
  // retry sends a new revision) does not drop the reader back to the first page needlessly.
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / CHAT_TABLE_ROWS_PER_PAGE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(
    currentPage * CHAT_TABLE_ROWS_PER_PAGE,
    (currentPage + 1) * CHAT_TABLE_ROWS_PER_PAGE,
  );
  // The caption used to print `rowCount` above however many rows the payload happened to
  // carry, so a 2,030-row result sliced to 50 was labelled "2030 row(s)". Three cases now,
  // and they say different things: the payload holds fewer rows than the query returned (we
  // know both numbers), the warehouse itself cut the result short (we know only what
  // arrived), or the table is complete.
  const isSliced = shown < total;
  return (
    <>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column} sx={{ fontWeight: 600 }}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row, rowIndex) => (
              <TableRow key={`row-${currentPage}-${rowIndex}`}>
                {columns.map((column) => (
                  <TableCell key={`${rowIndex}-${column}`}>
                    {formatCellValue(row[column])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {rows.length > CHAT_TABLE_ROWS_PER_PAGE ? (
        <TablePagination
          component="div"
          count={rows.length}
          page={currentPage}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={CHAT_TABLE_ROWS_PER_PAGE}
          rowsPerPageOptions={[CHAT_TABLE_ROWS_PER_PAGE]}
          labelDisplayedRows={({ from, to, count }) =>
            t("response.tables.pageOf", {
              from: from.toLocaleString(),
              to: to.toLocaleString(),
              count: count.toLocaleString(),
            })
          }
          sx={{
            color: "var(--theme-text-secondary)",
            "& .MuiTablePagination-toolbar": { minHeight: 36, px: 0 },
            "& .MuiTablePagination-displayedRows": { fontVariantNumeric: "tabular-nums" },
          }}
        />
      ) : null}
      <Typography
        variant="caption"
        sx={{ color: "var(--theme-text-secondary)" }}
      >
        {isSliced
          ? t("response.tables.rowCountTruncated", {
              shown: shown.toLocaleString(),
              total: total.toLocaleString(),
            })
          : truncated === true
            ? t("response.tables.rowCountCut", { count: total })
            : t("response.tables.rowCount", { count: total })}
      </Typography>
    </>
  );
}

function DataTable({
  tabularData,
  onAddToApp,
}: {
  tabularData: ChatTabularData;
  onAddToApp?: () => void;
}) {
  const { t } = useTranslation("chat");
  const additional = tabularData.additional_results ?? [];
  const failed = tabularData.failed_results ?? [];
  const [selected, setSelected] = useState(0);
  // An investigation answers with a baseline query plus the breakdowns that explain it, and
  // the insights quote the breakdowns. Showing only the first result left the reader unable
  // to check a single cited number.
  const tables: ChatResultTable[] = [tabularData, ...additional];
  const active = tables[Math.min(selected, tables.length - 1)];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {onAddToApp ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Tooltip title={t("response.tables.addToApp")}>
            <IconButton
              size="small"
              aria-label={t("response.tables.addToApp")}
              onClick={onAddToApp}
              sx={{ color: "var(--theme-text-secondary)" }}
            >
              <DashboardCustomizeOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
      {tables.length > 1 ? (
        <Tabs
          value={Math.min(selected, tables.length - 1)}
          onChange={(_event, value: number) => setSelected(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label={t("response.tables.resultSelector")}
          sx={{ minHeight: 0, "& .MuiTab-root": { minHeight: 0, py: 1 } }}
        >
          {tables.map((table, index) => (
            <Tab
              key={`result-${index}`}
              label={
                table.purpose && index > 0
                  ? table.purpose
                  : t("response.tables.resultLabel", { index: index + 1 })
              }
              sx={{ textTransform: "none" }}
            />
          ))}
        </Tabs>
      ) : null}
      <ResultTable key={`result-${Math.min(selected, tables.length - 1)}`} table={active} />
      {failed.length > 0 ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t("response.tables.failedResults", { count: failed.length })}
        </Alert>
      ) : null}
    </Box>
  );
}

function SqlQueriesPanel({
  queries,
  domainId,
}: {
  queries: GeneratedQuery[];
  domainId?: number | null;
}) {
  const { t } = useTranslation("chat");
  const dialect = useSqlDialect(domainId);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {queries.map((query, index) => (
        <Box key={`query-${index}`}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {query.purpose || `Query ${index + 1}`}
            </Typography>
            {query.cross_table_metric ? (
              <Tooltip
                title={
                  query.source_tables && query.source_tables.length > 0
                    ? t("response.sql.multiTableTooltip", {
                        tables: query.source_tables.join(", "),
                      })
                    : t("response.sql.multiTableTooltipGeneric")
                }
              >
                <Chip
                  size="small"
                  label={t("response.sql.multiTableBadge")}
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    bgcolor: "var(--theme-surface-hover)",
                    color: "var(--theme-text-secondary)",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              </Tooltip>
            ) : null}
          </Box>
          <Typography
            variant="caption"
            sx={{ color: "var(--theme-text-secondary)" }}
          >
            Status: {query.status || "unknown"}
            {typeof query.row_count === "number"
              ? ` · ${query.row_count} rows`
              : ""}
            {typeof query.execution_ms === "number"
              ? ` · ${query.execution_ms} ms`
              : ""}
          </Typography>
          {query.sql ? (
            <Box sx={{ mt: 1 }}>
              {/* One query is the whole SQL tab, so it is shown in full; several are
                  capped so the reader can see there is more than one. */}
              <SqlCodeMirror
                value={query.sql}
                readOnly
                maxHeight={queries.length === 1 ? "none" : "200px"}
                dialect={dialect}
              />
            </Box>
          ) : null}
          {query.error_message ? (
            <Typography
              variant="body2"
              sx={{ color: "var(--theme-error)", mt: 0.5 }}
            >
              {query.error_message}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function AvatarIcon() {
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "var(--theme-surface-hover)",
        color: "var(--theme-text-secondary)",
        flexShrink: 0,
      }}
      aria-hidden
    >
      <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
    </Box>
  );
}

function MessageBubble({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        justifyContent: "flex-start",
      }}
    >
      <Box
        sx={{
          borderRadius: 2,
          px: 2,
          py: 1.25,
          maxWidth: "72%",
          bgcolor: "var(--theme-surface-hover)",
          color: "var(--theme-text-primary)",
          border: "1px solid var(--theme-border)",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/** Shown over early table/chart/SQL until the answer validator approves them. */
function VerifyingAnswerNotice() {
  const { t } = useTranslation("chat");
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      role="status"
      sx={{
        mb: 1.5,
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        border: "1px solid color-mix(in srgb, var(--theme-info) 35%, transparent)",
        bgcolor: "color-mix(in srgb, var(--theme-info) 12%, transparent)",
      }}
    >
      <CircularProgress size={12} thickness={5} sx={{ color: "var(--theme-info)" }} />
      <Typography variant="caption" sx={{ color: "var(--theme-text-secondary)" }}>
        {t("response.parts.verifying")}
      </Typography>
    </Stack>
  );
}

/** Where why and action will appear, while they are written after the why research. */
function NarrativePendingNotice() {
  const { t } = useTranslation("chat");
  return (
    <Stack direction="row" spacing={1} alignItems="center" role="status" sx={{ px: 0.5 }}>
      <CircularProgress size={12} thickness={5} sx={{ color: "var(--theme-primary)" }} />
      <Typography variant="caption" sx={{ color: "var(--theme-text-secondary)" }}>
        {t("response.parts.researchingWhy")}
      </Typography>
    </Stack>
  );
}

/** The Insights tab before any answer text exists, while other parts may already be shown. */
function WritingInsightsNotice({ dataIsReady }: { dataIsReady: boolean }) {
  const { t } = useTranslation("chat");
  return (
    <Stack direction="row" spacing={1} alignItems="center" role="status" sx={{ py: 1 }}>
      <CircularProgress size={14} thickness={5} sx={{ color: "var(--theme-primary)" }} />
      <Typography variant="body2" sx={{ color: "var(--theme-text-secondary)" }}>
        {t(dataIsReady ? "response.parts.writingInsightsDataReady" : "response.parts.writingInsights")}
      </Typography>
    </Stack>
  );
}

function StreamingCursor() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 6,
        height: 14,
        ml: 0.25,
        bgcolor: "var(--theme-primary)",
        verticalAlign: "text-bottom",
        animation: "blink 1s step-end infinite",
        "@keyframes blink": {
          "50%": { opacity: 0 },
        },
      }}
      aria-hidden
    />
  );
}
